/**
 * The station's V6 "Role checks" card: the latest QA crawl (Bugsy) from the console and the
 * phone, one row per role. A role passes when neither crawl found a real problem for it; slow
 * pages, slow requests, writes the crawl itself stopped and outside calls the test server
 * blocked are noise, shown only as counts. Each row opens to its findings, in full.
 * The crawlers post to POST /api/pulse/qa-runs (src/aithora/pulse/qa-runs.ts); this reads
 * GET /api/pulse/qa-runs.
 */
import { useState } from 'react';
import { ROLES, ROLE_LABEL, type QaRun, type QaRunsReport } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { DevNote } from '../../ui/buildLabels';

/** Finding kinds in plain words (the crawlers' kinds, e2e/console-qa/crawl.mjs and e2e/qa/mobile-crawl.mjs). */
export const QA_KIND_WORDS: Record<string, string> = {
  http: 'A request failed',
  'http-403': 'A request was refused',
  network: 'A network call failed',
  console: 'An error in the browser',
  pageerror: 'The page threw an error',
  'native-dialog': 'A browser pop-up opened',
  'bad-text': 'Broken words on screen',
  'em-dash': 'An em dash on screen',
  'raw-id': 'A raw id on screen',
  'raw-json': 'Raw data on screen',
  'vendor-name': 'A vendor name on screen',
  'h-scroll': 'The page scrolls sideways',
  'still-loading': 'Still loading',
  clipped: 'Words cut off',
  spilled: 'Words spill out of their box',
  'off-screen': 'Something sits off screen',
  blank: 'The page is blank',
  'error-shown': 'An error shows on the page',
  'dead-control': 'A control does nothing',
  'atlas-no-answer': 'Atlas gave no answer',
  'crawl-error': 'The check could not finish',
  redirected: 'The page sent it somewhere else',
  'wrong-landing': 'It landed on the wrong first screen',
  'nav-mismatch': 'The wrong tabs show',
  'permission-screen': 'It opened a screen it should not',
  'off-box-request': 'The app called an outside site',
  'no-pages': 'No page was checked',
};
/** Noise the server keeps only as counts (src/aithora/pulse/qa-runs.ts). */
export const QA_NOISE_WORDS: Record<string, string> = {
  'slow-load': 'Slow pages',
  'slow-api': 'Slow requests',
  'write-on-safe-click': 'Writes the check stopped',
  'blocked-outbound-network': 'Outside calls the test server blocked',
};
const QA_SOURCES: { key: 'console' | 'mobile'; label: string }[] = [
  { key: 'console', label: 'Console' },
  { key: 'mobile', label: 'Phone' },
];
const qaWhen = (iso: string) => new Date(iso).toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const roleWord = (r: string) => (ROLE_LABEL as Record<string, string>)[r] ?? r;

export interface QaRoleRow {
  role: string;
  runs: { key: 'console' | 'mobile'; label: string; result: QaRun['roles'][number] | null }[];
  pass: boolean;
}

/** One row per role, in the stored role order: pass when neither crawl found a real problem for it. */
export function qaRoleRows(report: QaRunsReport | null): QaRoleRow[] {
  if (!report) return [];
  const names = new Set<string>();
  for (const { key } of QA_SOURCES) for (const r of report[key]?.roles ?? []) names.add(r.role);
  const order = (r: string) => {
    const i = (ROLES as readonly string[]).indexOf(r);
    return i < 0 ? ROLES.length : i;
  };
  return [...names]
    .sort((a, b) => order(a) - order(b) || a.localeCompare(b))
    .map((role) => {
      const runs = QA_SOURCES.filter(({ key }) => report[key]).map(({ key, label }) => ({ key, label, result: report[key]?.roles.find((x) => x.role === role) ?? null }));
      return { role, runs, pass: runs.every((x) => !x.result || x.result.status === 'pass') };
    });
}

export function RoleChecks({ report, loading, error, plain }: { report: QaRunsReport | null; loading: boolean; error: boolean; plain: (s: string, fallback?: string) => string }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const rows = qaRoleRows(report);
  const ran = QA_SOURCES.filter(({ key }) => report?.[key]);
  const passed = rows.filter((r) => r.pass).length;
  return (
    <div data-screen-label="Pulse · Role checks" data-testid="pulse-role-checks" style={sx('box-sizing:border-box;display:flex;flex-direction:column;gap:8px;margin-bottom:12px;border:1px solid rgba(34,39,46,.14);border-radius:12px;background:#FCFAF4;padding:10px 12px;min-width:0;')}>
      <div style={sx('display:flex;align-items:baseline;gap:8px 12px;flex-wrap:wrap;')}>
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#22272E;')}>Role checks</span>
        <span style={sx('font-size:16px;color:#54606C;')}>
          {error
            ? plain('The role checks could not be read (GET /api/pulse/qa-runs).', 'The role checks could not be read.')
            : rows.length === 0
              ? loading
                ? ''
                : 'No role check has run yet. Pulse will sign in as every role and walk each page.'
              : `${passed} of ${rows.length} roles passed in the last check.`}
        </span>
      </div>
      {!error && !loading && rows.length === 0 && (
        <DevNote>
          <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>Role checks appear once a QA crawl posts its results to Pulse.</span>
        </DevNote>
      )}
      {ran.length > 0 && (
        <div style={sx('display:flex;flex-direction:column;gap:2px;font-size:14px;color:#54606C;line-height:1.4;')}>
          {ran.map(({ key, label }) => {
            const run = report?.[key] as QaRun;
            return (
              <div key={key} data-testid={'pulse-role-checks-run-' + key} style={sx('display:flex;flex-wrap:wrap;gap:0 6px;min-width:0;')}>
                <span style={sx('font-weight:700;color:#22272E;')}>{label}</span>
                <span>last checked</span>
                <span>{qaWhen(run.finishedAt)}</span>
                <span>commit</span>
                <span translate="no" style={sx('font-variant-numeric:tabular-nums;overflow-wrap:anywhere;')}>
                  {run.commit}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {rows.length > 0 && (
        <div role="list" style={sx('display:flex;flex-direction:column;min-width:0;')}>
          {rows.map((row) => {
            const isOpen = !!open[row.role];
            return (
              <div key={row.role} role="listitem" data-testid="pulse-role-check" style={sx('border-top:1px solid rgba(34,39,46,.07);min-width:0;')}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpen((o) => ({ ...o, [row.role]: !o[row.role] }))}
                  style={sx('all:unset;box-sizing:border-box;cursor:pointer;display:flex;align-items:center;flex-wrap:wrap;gap:6px 10px;width:100%;min-height:32px;padding:4px 0;')}
                >
                  <span style={sx('flex:1 1 140px;min-width:0;font-size:16px;font-weight:700;color:#22272E;')}>{roleWord(row.role)}</span>
                  {row.runs.map((x) => {
                    const n = x.result?.findings.length ?? 0;
                    const tone = !x.result ? 'color:#54606C;background:rgba(34,39,46,.07);' : x.result.status === 'pass' ? 'color:#1F6B52;background:rgba(47,138,111,.13);' : 'color:#B3261E;background:rgba(179,38,30,.1);';
                    return (
                      <span key={x.key} style={sx('display:inline-flex;align-items:center;gap:5px;font-size:14px;color:#54606C;')}>
                        <span>{x.label}</span>
                        <span style={sx('font-size:14px;font-weight:800;letter-spacing:.06em;border-radius:4px;padding:2px 6px;' + tone)}>
                          {!x.result ? 'Not checked' : x.result.status === 'pass' ? 'PASS' : n === 1 ? '1 problem' : n + ' problems'}
                        </span>
                      </span>
                    );
                  })}
                </button>
                {isOpen && (
                  <div data-testid="pulse-role-check-findings" style={sx('display:flex;flex-direction:column;gap:6px;padding:2px 0 10px;min-width:0;')}>
                    {row.runs.map((x) => (
                      <div key={x.key} style={sx('display:flex;flex-direction:column;gap:3px;min-width:0;')}>
                        <span style={sx('font-size:14px;letter-spacing:.08em;text-transform:uppercase;font-weight:700;color:#54606C;')}>{x.label}</span>
                        {!x.result ? (
                          <span style={sx('font-size:16px;color:#54606C;')}>This role was not in this check.</span>
                        ) : x.result.findings.length === 0 ? (
                          <span style={sx('font-size:16px;color:#1F6B52;')}>{x.result.pages.length === 1 ? '1 page checked, no problems.' : x.result.pages.length + ' pages checked, no problems.'}</span>
                        ) : (
                          x.result.findings.map((f, i) => (
                            <div key={i} data-testid="pulse-role-check-finding" style={sx('display:flex;flex-direction:column;gap:1px;padding:5px 8px;border-radius:7px;background:rgba(179,38,30,.05);min-width:0;')}>
                              <span style={sx('font-size:16px;line-height:1.4;color:#22272E;overflow-wrap:anywhere;')}>
                                <span style={sx('font-weight:700;')}>{QA_KIND_WORDS[f.kind] ?? f.kind}</span>
                                {' · '}
                                <span translate="no">{f.page}</span>
                              </span>
                              <span translate="no" style={sx('font-size:14px;line-height:1.4;color:#3C454F;overflow-wrap:anywhere;white-space:pre-wrap;')}>
                                {f.detail}
                              </span>
                              {f.sizes.length > 0 && (
                                <span translate="no" style={sx('font-size:14px;color:#54606C;')}>
                                  {f.sizes.join(', ')}
                                </span>
                              )}
                            </div>
                          ))
                        )}
                        {x.result && Object.keys(x.result.noise).length > 0 && (
                          <span style={sx('display:flex;flex-wrap:wrap;gap:0 8px;font-size:14px;color:#54606C;')}>
                            <span>Not counted as problems:</span>
                            {Object.entries(x.result.noise).map(([k, n]) => (
                              <span key={k}>
                                <span>{QA_NOISE_WORDS[k] ?? k}</span> <span>{n}</span>
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
